import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URLS from '../../config/api';
import axiosInstance from '../../utils/axios';

const VehicleManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear().toString(),
    color: '',
    licensePlate: '',
    capacity: '',
    vehicleImage: null
  });

  // Generate an array of years from 1990 to current year
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1989 }, (_, i) => (currentYear - i).toString());

  // Popular makes sorted by popularity
  const vehicleData = [
    { make: 'Ford', models: ['F-150', 'Mustang', 'Escape', 'Explorer', 'Edge', 'Fusion', 'Focus', 'Ranger', 'Expedition'] },
    { make: 'Honda', models: ['Civic', 'Accord', 'CR-V', 'Pilot', 'Fit', 'Odyssey', 'HR-V', 'Ridgeline'] },
    { make: 'Toyota', models: ['RAV4', 'Corolla', 'Camry', 'Highlander', 'Tacoma', 'Sienna', 'Prius', '4Runner'] },
    { make: 'Chevrolet', models: ['Silverado', 'Equinox', 'Malibu', 'Traverse', 'Tahoe', 'Camaro', 'Colorado'] },
    { make: 'BMW', models: ['3 Series', 'X5', 'X3', '5 Series', '4 Series', 'X1', 'X7'] },
    { make: 'Mercedes-Benz', models: ['C-Class', 'E-Class', 'GLC', 'GLE', 'GLA', 'S-Class'] },
    { make: 'Jeep', models: ['Wrangler', 'Grand Cherokee', 'Cherokee', 'Compass', 'Renegade', 'Gladiator'] },
    { make: 'Mazda', models: ['CX-5', 'Mazda3', 'Mazda6', 'CX-9', 'CX-30', 'MX-5'] },
    { make: 'Hyundai', models: ['Elantra', 'Tucson', 'Santa Fe', 'Sonata', 'Kona', 'Palisade'] },
    { make: 'Volkswagen', models: ['Jetta', 'Golf', 'Tiguan', 'Passat', 'Atlas', 'Beetle'] },
    { make: 'Nissan', models: ['Rogue', 'Altima', 'Sentra', 'Murano', 'Pathfinder', 'Frontier'] },
    { make: 'Kia', models: ['Sorento', 'Sportage', 'Forte', 'Soul', 'Seltos', 'Telluride'] },
    { make: 'Subaru', models: ['Outback', 'Forester', 'Impreza', 'Crosstrek', 'Ascent', 'Legacy'] },
    { make: 'Acura', models: ['MDX', 'RDX', 'TLX', 'ILX', 'Integra'] },
    { make: 'Audi', models: ['A4', 'Q5', 'A3', 'Q7', 'Q3', 'A6'] },
    { make: 'Lexus', models: ['RX', 'NX', 'ES', 'IS', 'GX', 'UX'] },
    { make: 'GMC', models: ['Sierra', 'Terrain', 'Acadia', 'Canyon', 'Yukon'] },
    { make: 'Dodge', models: ['Grand Caravan', 'Charger', 'Durango', 'Challenger', 'Journey'] },
    { make: 'Ram', models: ['1500', '2500', '3500', 'ProMaster'] },
    { make: 'Porsche', models: ['911', 'Cayenne', 'Macan', 'Panamera', 'Taycan'] },
    // ... add more as needed
  ];

  const popularMakes = vehicleData.map(v => v.make).sort();

  const getModelsForMake = (make) => {
    const found = vehicleData.find(v => v.make === make);
    return found ? found.models : [];
  };

  const [modelOptions, setModelOptions] = useState([]);

  useEffect(() => {
    if (formData.make) {
      setModelOptions(getModelsForMake(formData.make));
      // Reset model if make changes
      setFormData(prev => ({ ...prev, model: '' }));
    } else {
      setModelOptions([]);
      setFormData(prev => ({ ...prev, model: '' }));
    }
    // eslint-disable-next-line
  }, [formData.make]);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axiosInstance.get(API_URLS.vehicles);
      setVehicles(response.data);
    } catch (error) {
      setError('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Send the data directly without FormData since we're not handling file upload yet
      const vehicleData = {
        make: formData.make,
        model: formData.model,
        year: formData.year,
        color: formData.color,
        licensePlate: formData.licensePlate,
        capacity: formData.capacity
      };

      console.log('Sending vehicle data:', vehicleData); // Debug log

      await axiosInstance.post(API_URLS.vehicles, vehicleData);
      setShowAddForm(false);
      setFormData({
        make: '',
        model: '',
        year: new Date().getFullYear().toString(),
        color: '',
        licensePlate: '',
        capacity: '',
        vehicleImage: null
      });
      fetchVehicles();
    } catch (error) {
      console.error('Vehicle creation error:', error.response?.data || error);
      setError(error.response?.data?.message || 'Failed to add vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axiosInstance.put(`${API_URLS.vehicles}/${id}/default`);
      fetchVehicles();
    } catch (error) {
      setError('Failed to set default vehicle');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">My Vehicles</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Add Vehicle
        </button>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 p-4 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {showAddForm && (
        <div className="mt-8 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Add New Vehicle</h3>
            <form onSubmit={handleSubmit} className="mt-5 space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Make</label>
                  <select
                    name="make"
                    required
                    value={formData.make}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Select Make</option>
                    {popularMakes.map(make => (
                      <option key={make} value={make}>{make}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Model</label>
                  <select
                    name="model"
                    required
                    value={formData.model}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    disabled={!formData.make}
                  >
                    <option value="">{formData.make ? 'Select Model' : 'Select Make First'}</option>
                    {modelOptions.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Year</label>
                  <select
                    name="year"
                    required
                    value={formData.year}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Color</label>
                  <input
                    type="text"
                    name="color"
                    required
                    value={formData.color}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">License Plate</label>
                  <input
                    type="text"
                    name="licensePlate"
                    required
                    value={formData.licensePlate}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Capacity</label>
                  <input
                    type="number"
                    name="capacity"
                    required
                    value={formData.capacity}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Vehicle Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleInputChange}
                    className="mt-1 block w-full"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Save Vehicle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && !showAddForm ? (
        <div className="mt-8 text-center">Loading...</div>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {vehicles.map((vehicle) => (
            <div
              key={vehicle.id}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              {vehicle.vehicleImage && (
                <div className="h-48 w-full bg-gray-200">
                  <img
                    src={vehicle.vehicleImage}
                    alt={`${vehicle.make} ${vehicle.model}`}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {vehicle.make} {vehicle.model} ({vehicle.year})
                </h3>
                <dl className="mt-2 divide-y divide-gray-200">
                  <div className="py-3 flex justify-between text-sm">
                    <dt className="text-gray-500">Color:</dt>
                    <dd className="text-gray-900">{vehicle.color}</dd>
                  </div>
                  <div className="py-3 flex justify-between text-sm">
                    <dt className="text-gray-500">License Plate:</dt>
                    <dd className="text-gray-900">{vehicle.licensePlate}</dd>
                  </div>
                  <div className="py-3 flex justify-between text-sm">
                    <dt className="text-gray-500">Capacity:</dt>
                    <dd className="text-gray-900">{vehicle.capacity} seats</dd>
                  </div>
                </dl>
                {!vehicle.isDefault && (
                  <button
                    onClick={() => handleSetDefault(vehicle.id)}
                    className="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Set as Default
                  </button>
                )}
                {vehicle.isDefault && (
                  <div className="mt-4 text-center text-sm text-indigo-600 font-medium">
                    Default Vehicle
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VehicleManagement;
